import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { listRecentAuditEvents, type AdminAuditEvent } from '@/lib/adminBilling';
import { humanize } from '@/lib/errors';
import { formatDate } from '@/lib/dates';

/**
 * /admin/audit — cross-org audit feed. Owner /audit is org-scoped; this is
 * platform-wide and admin-only. Filters are client-side over the last 100
 * events — for deeper history with date range / actor filters, see task #44.
 */
export default function AdminAuditFeed() {
  const [events, setEvents] = useState<AdminAuditEvent[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [orgFilter, setOrgFilter] = useState<string>(''); // '' = all
  const [typeFilter, setTypeFilter] = useState<string>(''); // '' = all

  useEffect(() => {
    let cancelled = false;
    listRecentAuditEvents(100)
      .then((e) => { if (!cancelled) { setEvents(e); setLoading(false); } })
      .catch((err) => { if (!cancelled) { setError(humanize(err)); setLoading(false); } });
    return () => { cancelled = true; };
  }, []);

  // Build distinct-value lists for the dropdowns from the loaded events.
  const orgOptions = useMemo(() => {
    const m = new Map<string, string>(); // id -> name
    for (const e of events) {
      if (e.organization_id) {
        m.set(e.organization_id, e.org_display_name ?? e.organization_id.slice(0, 8));
      }
    }
    return Array.from(m.entries())
      .sort(([, a], [, b]) => a.localeCompare(b))
      .map(([id, name]) => ({ id, name }));
  }, [events]);

  const typeOptions = useMemo(() => {
    return Array.from(new Set(events.map((e) => e.event_type))).sort();
  }, [events]);

  const filtered = useMemo(() => {
    return events.filter((e) =>
      (orgFilter === '' || e.organization_id === orgFilter) &&
      (typeFilter === '' || e.event_type === typeFilter)
    );
  }, [events, orgFilter, typeFilter]);

  if (loading) return <p className="text-sm text-muted-foreground">Loading audit events…</p>;
  if (error)   return <p className="text-sm text-red-600">{error}</p>;

  return (
    <div className="space-y-4">
      <header className="space-y-1">
        <h2 className="text-2xl font-semibold tracking-tight">Audit feed (platform-wide)</h2>
        <p className="text-sm text-muted-foreground">
          Last {events.length} events across all organizations, newest first.
          {(orgFilter || typeFilter) && (
            <> Showing <strong>{filtered.length}</strong> after filter.</>
          )}
        </p>
      </header>

      <div className="flex flex-wrap items-end gap-3 rounded-lg border border-border bg-card p-3">
        <label className="text-xs space-y-1">
          <span className="block text-muted-foreground uppercase tracking-wider">Organization</span>
          <select
            value={orgFilter}
            onChange={(e) => setOrgFilter(e.target.value)}
            className="rounded-md border border-border px-2 py-1 text-sm min-w-[200px]"
          >
            <option value="">All organizations</option>
            {orgOptions.map((o) => (
              <option key={o.id} value={o.id}>{o.name}</option>
            ))}
          </select>
        </label>
        <label className="text-xs space-y-1">
          <span className="block text-muted-foreground uppercase tracking-wider">Event type</span>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="rounded-md border border-border px-2 py-1 text-sm min-w-[200px]"
          >
            <option value="">All event types</option>
            {typeOptions.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </label>
        {(orgFilter || typeFilter) && (
          <button
            type="button"
            onClick={() => { setOrgFilter(''); setTypeFilter(''); }}
            className="text-xs underline text-muted-foreground self-center"
          >
            Clear filters
          </button>
        )}
      </div>

      <div className="rounded-lg border border-border bg-card overflow-x-auto">
        <table className="w-full text-sm border-collapse min-w-[760px]">
          <thead>
            <tr className="border-b border-border text-left">
              <th className="p-3 text-xs uppercase tracking-wider text-muted-foreground font-medium">When</th>
              <th className="p-3 text-xs uppercase tracking-wider text-muted-foreground font-medium">Event</th>
              <th className="p-3 text-xs uppercase tracking-wider text-muted-foreground font-medium">Actor</th>
              <th className="p-3 text-xs uppercase tracking-wider text-muted-foreground font-medium">Org</th>
              <th className="p-3 text-xs uppercase tracking-wider text-muted-foreground font-medium">Summary</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((e, i) => (
              <tr key={e.event_id} className={`border-b border-border ${i % 2 === 0 ? '' : 'bg-muted/10'}`}>
                <td className="p-3 align-top text-xs">{formatDate(e.occurred_at)}</td>
                <td className="p-3 align-top text-xs" style={{ color: 'hsl(var(--brand-navy))' }}>{e.event_type}</td>
                <td className="p-3 align-top text-xs">{e.actor_email ?? '—'}</td>
                <td className="p-3 align-top text-xs">
                  {e.organization_id ? (
                    <Link to={`/admin/orgs/${e.organization_id}`} className="hover:underline">
                      {e.org_display_name ?? e.organization_id.slice(0, 8)}
                    </Link>
                  ) : '—'}
                </td>
                <td className="p-3 align-top text-sm">{e.summary}</td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={5} className="p-6 text-center text-sm text-muted-foreground">
                {events.length === 0 ? 'No events yet.' : 'No events match the current filters.'}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
